/**
 * User preference cache helpers — pure state utilities for keyed preferences.
 *
 * Shared by the Redux slice and unit tests so preference hydration rules stay
 * explicit: all-user loads, prefix loads, and exact-key loads can each satisfy
 * a hook without triggering another network request.
 */
export function normalizeUserPreferenceResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

export function mergePreferenceRecords(existing = {}, records = []) {
  const next = { ...existing };
  records.forEach(record => {
    if (!record?.key) return;
    next[record.key] = record;
  });
  return next;
}

const EMPTY_STATUS = Object.freeze({ loaded: false, loading: false, error: null });

function getMatchingPrefixStatus(state, key) {
  const statuses = state?.prefixStatus ?? {};
  return Object.keys(statuses)
    .filter(prefix => key.startsWith(prefix))
    .sort((a, b) => b.length - a.length)
    .map(prefix => statuses[prefix])
    .find(Boolean);
}

export function getPreferenceHydration(state, key) {
  if (!key) return { record: null, loaded: true, loading: false };

  const record = state?.byKey?.[key] ?? null;
  if (record) return { record, loaded: true, loading: false };

  if (state?.allLoading) return { record: null, loaded: false, loading: true };
  if (state?.allLoaded) return { record: null, loaded: true, loading: false };

  const prefixStatus = getMatchingPrefixStatus(state, key);
  if (prefixStatus?.loading) return { record: null, loaded: false, loading: true };
  if (prefixStatus?.loaded) return { record: null, loaded: true, loading: false };

  const keyStatus = state?.keyStatus?.[key] ?? EMPTY_STATUS;
  return {
    record: null,
    loaded: Boolean(keyStatus.loaded),
    loading: Boolean(keyStatus.loading),
  };
}

export function resolvePreferenceValue({ hydration, currentValue, defaultValue }) {
  if (hydration?.record) return hydration.record.value;
  if (hydration?.loaded) return defaultValue;
  return currentValue;
}
